import React, { useState, useCallback } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import CaptchaField from '../../components/base/CaptchaField';

const CareersPage = () => {
  const [selectedJob, setSelectedJob] = useState<string | null>(null);

  const jobs = [
    {
      id: 'ceo-nexa-prime',
      title: 'Chief Executive Officer (CEO)',
      department: 'Physical AI & IT Tech',
      location: 'Kuala Lumpur, Malaysia',
      type: 'Full-time',
      description:
        'Lead Nexa Prime Sdn Bhd as CEO, driving the Physical AI vision across all Drixem Group divisions. Spearhead the integration of AI with hardware, logistics, and industrial infrastructure at massive scale.',
      responsibilities: [
        'Define and execute the overall Physical AI strategy for Nexa Prime and the Drixem Group ecosystem',
        'Lead business development, partnerships, and investor relations for the AI IT Tech division',
        'Oversee product development, technology roadmap, and team growth',
        'Drive revenue growth and position Nexa Prime for potential public listing',
      ],
      requirements: [
        'Proven C-level or senior executive experience in AI, technology, or industrial automation',
        '10+ years of leadership experience in technology or related industries',
        'Strong understanding of Physical AI, IoT, and industrial infrastructure',
        'Exceptional strategic thinking, communication, and stakeholder management skills',
      ],
      icon: 'ri-user-star-line',
    },
    {
      id: 'software-dev',
      title: 'Software Developer',
      department: 'Physical AI & IT Tech',
      location: 'Kuala Lumpur, Malaysia',
      type: 'Full-time',
      description:
        'Join our Physical AI technology team to build cutting-edge AI solutions, automation platforms, and digital products.',
      responsibilities: [
        'Develop and maintain web applications using modern frameworks',
        'Collaborate with cross‑functional teams on product development',
        'Write clean, maintainable, and efficient code',
        'Participate in code reviews and technical discussions',
      ],
      requirements: [
        "Bachelor's degree in Computer Science or related field",
        '3+ years of experience in software development',
        'Proficiency in React, Node.js, and TypeScript',
        'Experience with cloud platforms (AWS, Azure, or GCP)',
      ],
      icon: 'ri-code-box-line',
    },
    {
      id: 'export-exec',
      title: 'Export Executive',
      department: 'B2B Export Trade',
      location: 'Seremban, Negeri Sembilan',
      type: 'Full-time',
      description:
        'Manage international trade operations at Aravion Zeus Sdn Bhd and expand our export network across five continents.',
      responsibilities: [
        'Coordinate export operations and logistics',
        'Build and maintain relationships with international buyers',
        'Ensure compliance with export regulations and certifications',
        'Negotiate contracts and pricing with global partners',
      ],
      requirements: [
        "Bachelor's degree in International Business or related field",
        '2+ years of experience in export operations',
        'Knowledge of palm oil industry and trade regulations',
        'Excellent communication and negotiation skills',
      ],
      icon: 'ri-ship-line',
    },
    {
      id: 'operations-consultant',
      title: 'Operations Consultant',
      department: 'Palm Oil',
      location: 'Senai, Johor',
      type: 'Full-time',
      description:
        'Provide expert guidance on palm oil operations and supply chain excellence for Drixem Mega Sdn Bhd at our Senai office and factory.',
      responsibilities: [
        'Assess operations and recommend improvements',
        'Implement sustainable practices across the supply chain',
        'Monitor quality control and yield optimization',
        'Ensure compliance with MPOB and environmental standards',
      ],
      requirements: [
        "Bachelor's degree in Agriculture, Supply Chain, or related field",
        '5+ years of experience in palm oil operations management',
        'Knowledge of MPOB standards and certifications',
        'Strong analytical and problem\u2011solving skills',
      ],
      icon: 'ri-plant-line',
    },
    {
      id: 'product-manager',
      title: 'Product Manager',
      department: 'Physical AI & IT Tech',
      location: 'Kuala Lumpur, Malaysia',
      type: 'Full-time',
      description:
        'Lead product strategy and development for our digital platforms including KL Carpool and the B2B marketplace.',
      responsibilities: [
        'Define product vision and roadmap',
        'Gather and prioritize product requirements',
        'Work with engineering teams to deliver features',
        'Analyze user feedback and market trends',
      ],
      requirements: [
        "Bachelor's degree in Business or related field",
        '4+ years of product management experience',
        'Experience with SaaS and marketplace platforms',
        'Strong leadership and communication skills',
      ],
      icon: 'ri-lightbulb-line',
    },
    {
      id: 'manufacturing-engineer',
      title: 'Manufacturing Engineer',
      department: 'Manufacturing',
      location: 'Senai, Johor',
      type: 'Full-time',
      description:
        'Oversee production processes at Drixem Nexus Manufacturing Sdn Bhd and drive operational efficiency improvements.',
      responsibilities: [
        'Manage and optimize manufacturing production lines',
        'Implement quality control and assurance processes',
        'Coordinate with supply chain and logistics teams',
        'Drive continuous improvement initiatives',
      ],
      requirements: [
        "Bachelor's degree in Engineering or related field",
        '3+ years of manufacturing experience',
        'Knowledge of OEM manufacturing processes',
        'Strong problem‑solving and analytical skills',
      ],
      icon: 'ri-settings-3-line',
    },
    {
      id: 'marketing-exec',
      title: 'Marketing Executive',
      department: 'Advertising & Marketing',
      location: 'Seremban, Negeri Sembilan',
      type: 'Full-time',
      description:
        'Drive brand awareness and marketing strategies at Drixem Sdn Bhd across our divisions and digital platforms.',
      responsibilities: [
        'Develop and execute marketing campaigns',
        'Manage digital marketing channels and content',
        'Analyze campaign performance and ROI',
        'Collaborate with sales and product teams',
      ],
      requirements: [
        "Bachelor's degree in Marketing or related field",
        '2+ years of marketing experience',
        'Experience with B2B and digital marketing',
        'Strong creative and analytical skills',
      ],
      icon: 'ri-megaphone-line',
    },
    {
      id: 'property-exec',
      title: 'Property Development Executive',
      department: 'Property Development',
      location: 'Seremban, Negeri Sembilan',
      type: 'Full-time',
      description:
        'Support property development projects at Heuliez (M) Sdn Bhd including land acquisition and project management.',
      responsibilities: [
        'Assist in land acquisition and due diligence',
        'Coordinate with contractors and consultants',
        'Monitor project timelines and budgets',
        'Prepare project reports and presentations',
      ],
      requirements: [
        "Bachelor's degree in Real Estate, Architecture, or related field",
        '2+ years of property development experience',
        'Knowledge of Malaysian property regulations',
        'Strong organizational and communication skills',
      ],
      icon: 'ri-building-line',
    },
    {
      id: 'investment-analyst',
      title: 'Investment Analyst',
      department: 'Investor Relations',
      location: 'Kuala Lumpur, Malaysia',
      type: 'Full-time',
      description:
        'Support investment evaluation and portfolio management across our six core business divisions.',
      responsibilities: [
        'Conduct financial analysis and due diligence',
        'Prepare investment reports and presentations',
        'Monitor portfolio performance and market trends',
        'Support investor relations activities',
      ],
      requirements: [
        "Bachelor's degree in Finance or related field",
        '2+ years of experience in investment analysis',
        'Strong financial modeling and analytical skills',
        'CFA or similar certification preferred',
      ],
      icon: 'ri-line-chart-line',
    },
  ];

  const benefits = [
    {
      icon: 'ri-trophy-line',
      title: 'Equity Rewards to High Achiever',
      description: 'Top performers earn equity participation, aligning personal success with company growth',
    },
    {
      icon: 'ri-graduation-cap-line',
      title: 'Learning & Development',
      description: 'Continuous training and professional development opportunities',
    },
    {
      icon: 'ri-calendar-line',
      title: 'Work-Life Balance',
      description: 'Flexible working arrangements and generous leave policies',
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Performance Rewards',
      description: 'Competitive salary with performance bonuses and incentives',
    },
  ];

  const locations = [
    { city: 'Seremban', state: 'Negeri Sembilan', desc: 'Corporate HQ & Operations' },
    { city: 'Senai', state: 'Johor', desc: 'Office & Factory' },
    { city: 'Kuala Lumpur', state: 'Federal Territory', desc: 'Tech, Investment & Digital' },
  ];

  const handleApply = (jobId: string) => {
    setSelectedJob(jobId);
    const element = document.getElementById('application-form');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      <section className="relative pt-0 pb-0">
        <div className="relative w-full min-h-[320px] md:min-h-[380px]">
          <img
            src="https://public.readdy.ai/ai/img_res/5a8225fdaab0315537b1633a2e963919.jpg"
            alt="Join Our Malaysian Team"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
          <div className="relative z-10 flex items-center justify-center min-h-[320px] md:min-h-[380px] py-20 px-4 sm:px-6">
            <div className="text-center w-full max-w-4xl mx-auto">
              <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-white mb-3 sm:mb-4 leading-tight">
                Shape the Future With Us
              </h1>
              <p className="text-sm sm:text-lg md:text-xl text-white/80 max-w-3xl mx-auto leading-relaxed px-2">
                Join a <strong>high-growth Malaysian conglomerate</strong> where innovation meets opportunity — your career breakthrough starts here
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-display font-bold text-gray-900 mb-4">
              Our Locations
            </h2>
            <p className="text-gray-600">Opportunities across Malaysia</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {locations.map((loc, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-xl transition-all"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-4">
                  <i className="ri-map-pin-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-1">{loc.city}</h3>
                <p className="text-sm text-primary-600 font-semibold mb-2">{loc.state}</p>
                <p className="text-sm text-gray-600">{loc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Current Openings
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Explore opportunities across our six business divisions
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="bg-white border border-gray-200 rounded-2xl p-8 hover:shadow-xl transition-all"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg flex-shrink-0">
                      <i className={`${job.icon} text-2xl text-primary-600`}></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{job.title}</h3>
                      <div className="flex flex-wrap gap-2">
                        <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold whitespace-nowrap">
                          {job.department}
                        </span>
                        <span className="inline-block px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold whitespace-nowrap">
                          {job.type}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-600 mb-4">
                  <i className="ri-map-pin-line"></i>
                  <span>{job.location}</span>
                </div>

                <p className="text-gray-600 mb-6 leading-relaxed text-sm">{job.description}</p>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">
                    Key Responsibilities
                  </h4>
                  <ul className="space-y-2">
                    {job.responsibilities.slice(0, 3).map((resp, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <div className="w-5 h-5 flex items-center justify-center mt-0.5">
                          <i className="ri-check-line text-primary-600 text-sm"></i>
                        </div>
                        <span className="text-sm text-gray-600">{resp}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={() => handleApply(job.id)}
                  className="w-full px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  Apply Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
              Why Work With Us?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Competitive benefits and a culture that values your growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-2xl p-8 text-center hover:shadow-xl transition-all"
              >
                <div className="w-16 h-16 flex items-center justify-center bg-primary-100 rounded-full mx-auto mb-6">
                  <i className={`${benefit.icon} text-3xl text-primary-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-sm text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="application-form" className="py-24 bg-gray-50">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-display font-bold text-gray-900 mb-6">
              Apply Now
            </h2>
            <p className="text-xl text-gray-600">
              Submit your application and join our growing team
            </p>
          </div>
          <CareerApplicationForm selectedJob={selectedJob} jobs={jobs} />
        </div>
      </section>

      <Footer />
    </div>
  );
};

type JobOption = {
  id: string;
  title: string;
};

const CareerApplicationForm = ({
  selectedJob,
  jobs,
}: {
  selectedJob: string | null;
  jobs: JobOption[];
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    position: selectedJob || '',
    linkedin: '',
    experience: '',
    resume: '',
    cover_letter: '',
  });
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error' | 'captcha'>('idle');

  const handleCaptchaVerified = useCallback((verified: boolean) => {
    setCaptchaVerified(verified);
    if (verified) setSubmitStatus('idle');
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!captchaVerified) {
      setSubmitStatus('captcha');
      return;
    }

    if (formData.cover_letter.length > 500) {
      setSubmitStatus('error');
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('position', formData.position);
      formBody.append('linkedin', formData.linkedin);
      formBody.append('experience', formData.experience);
      formBody.append('resume', formData.resume ? 'Uncollectable' : '');
      formBody.append('cover_letter', formData.cover_letter);

      const response = await fetch('https://readdy.ai/api/form/d6agvhnmvg9ih2c7ajg0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          position: '',
          linkedin: '',
          experience: '',
          resume: '',
          cover_letter: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form
      id="career-application-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="career-name" className="block text-sm font-semibold text-gray-900 mb-2">
            Full Name *
          </label>
          <input
            type="text"
            id="career-name"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="career-email" className="block text-sm font-semibold text-gray-900 mb-2">
            Email Address *
          </label>
          <input
            type="email"
            id="career-email"
            name="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="career-phone" className="block text-sm font-semibold text-gray-900 mb-2">
            Phone Number *
          </label>
          <input
            type="tel"
            id="career-phone"
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label htmlFor="career-position" className="block text-sm font-semibold text-gray-900 mb-2">
            Position Applied For *
          </label>
          <select
            id="career-position"
            name="position"
            value={formData.position}
            onChange={(e) => setFormData({ ...formData, position: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          >
            <option value="">Select a position</option>
            {jobs.map((job) => (
              <option key={job.id} value={job.id}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        <div>
          <label htmlFor="career-linkedin" className="block text-sm font-semibold text-gray-900 mb-2">
            LinkedIn Profile
          </label>
          <input
            type="url"
            id="career-linkedin"
            name="linkedin"
            value={formData.linkedin}
            onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
            placeholder="https://linkedin.com/in/yourprofile"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          />
        </div>
        <div>
          <label
            htmlFor="career-experience"
            className="block text-sm font-semibold text-gray-900 mb-2"
          >
            Years of Experience *
          </label>
          <select
            id="career-experience"
            name="experience"
            value={formData.experience}
            onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
            required
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm"
          >
            <option value="">Select experience</option>
            <option value="0-2">0-2 years</option>
            <option value="2-5">2-5 years</option>
            <option value="5-10">5-10 years</option>
            <option value="10+">10+ years</option>
          </select>
        </div>
      </div>

      <div className="mb-6">
        <label htmlFor="career-resume" className="block text-sm font-semibold text-gray-900 mb-2">
          Upload Resume *
        </label>
        <div className="relative">
          <input
            type="file"
            id="career-resume"
            name="resume"
            accept=".pdf,.doc,.docx"
            required
            onChange={(e) => {
              const file = e.target.files?.[0];
              setFormData({ ...formData, resume: file ? file.name : '' });
            }}
            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 cursor-pointer"
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX (Max 5MB)</p>
      </div>

      <div className="mb-6">
        <label htmlFor="career-cover" className="block text-sm font-semibold text-gray-900 mb-2">
          Cover Letter *
        </label>
        <textarea
          id="career-cover"
          name="cover_letter"
          value={formData.cover_letter}
          onChange={(e) => setFormData({ ...formData, cover_letter: e.target.value })}
          required
          rows={6}
          maxLength={500}
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent text-sm resize-none"
          placeholder="Tell us why you are interested in this position..."
        ></textarea>
        <div className="text-xs text-gray-500 mt-1">
          {formData.cover_letter.length}/500 characters
        </div>
      </div>

      <CaptchaField onVerified={handleCaptchaVerified} />

      {submitStatus === 'success' && (
        <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
          <p className="text-sm text-primary-700">
            Thank you for your application! Our HR team will review your submission and contact
            you soon.
          </p>
        </div>
      )}
      {submitStatus === 'error' && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-700">
            There was an error submitting your application. Please try again or WhatsApp us at
            +6012-2353795.
          </p>
        </div>
      )}
      {submitStatus === 'captcha' && (
        <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-sm text-amber-700">
            Please complete the security verification before submitting.
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full px-8 py-4 bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-all whitespace-nowrap cursor-pointer"
      >
        {isSubmitting ? 'Submitting...' : 'Submit Application'}
      </button>
    </form>
  );
};

export default CareersPage;
